import { Box, styled, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { memo, useEffect, useState } from "react";
import { CarSelectOptionField, CarTextField } from "@app/components/Inputs";
import { CarFlatPageControl } from "@app/components/FlatPageControl";
import { isDefined } from "@app/utils";
import { useSimulationModelsModels } from "./useSimulationModelsModels";
import { CarDownloadButton } from "@app/components/Buttons";
import {
  SIMULATION_MODELS_CHART_HEIGHT,
  SimulationModelsChart,
} from "./SimulationModelsChart";
import { chartsColor } from "@app/theme";
import { CarPageContent } from "@app/components/PageContent";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarAssetClassSelector } from "@app/components/AssetClassSelector";
import { CarStatBox } from "@app/components/StatBox";
import { SimulationFittedModelsChart } from "./SimulationFittedModelsChart";
import { CarDateField } from "@app/components/DateField";
import { CarExcelTable } from "@app/components/ExcelTable";

const StyledBox = styled(Box)(({ theme }) => ({
  border: "solid 1px",
  borderColor: theme.palette.gray6,
  borderRadius: "5px",
  backgroundColor: theme.palette.white,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(5.5),
}));

interface SimulationModelsModelsProps {
  isStatic: boolean;
}

const Legend = () => (
  <Box
    sx={{
      mt: 3,
      minHeight: 80,
      display: "flex",
      justifyContent: "space-around",
      border: "2px solid",
      borderColor: "gray3",
      borderRadius: "5px",
      backgroundColor: "white",
      alignItems: "center",
      px: 6,
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "softBlack", textAlign: "center" }}
      >
        <Box
          sx={{
            display: "inline-block",
            height: 3,
            width: 18,
            backgroundColor: "softBlack",
            borderRadius: "2px",
            mb: 0.3,
            mr: 1.5,
          }}
        />
        Trend Line for Spread Data
      </Typography>
    </Box>
    <Box
      sx={{ borderRight: "1px solid", borderRightColor: "gray3", height: 60 }}
    />
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "softBlack", textAlign: "center" }}
        >
          <Box
            sx={{
              mb: -0.6,
              mr: 1.5,
              display: "inline-block",
              height: 20,
              width: 20,
              backgroundColor: chartsColor.blue,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "gray6",
            }}
          />
          Historical Points
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "softBlack", textAlign: "center" }}
        >
          <Box
            sx={{
              mb: -0.6,
              mr: 1.5,
              display: "inline-block",
              height: 20,
              width: 20,
              backgroundColor: chartsColor.red,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "gray6",
            }}
          />
          Most Recent Point
        </Typography>
      </Box>
    </Box>
  </Box>
);

const FittedLegend = () => (
  <Box
    sx={{
      mt: 3,
      minHeight: 80,
      display: "flex",
      justifyContent: "space-around",
      border: "2px solid",
      borderColor: "gray3",
      borderRadius: "5px",
      backgroundColor: "white",
      alignItems: "center",
      px: 6,
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "softBlack", textAlign: "center" }}
      >
        <Box
          sx={{
            mb: -0.6,
            mr: 1.5,
            display: "inline-block",
            height: 20,
            width: 20,
            backgroundColor: "softBlack",
            borderRadius: "50%",
            border: "1px solid",
            borderColor: "gray6",
          }}
        />
        Fitted One Year Commodity
      </Typography>
    </Box>
    <Box
      sx={{ borderRight: "1px solid", borderRightColor: "gray3", height: 60 }}
    />
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "softBlack", textAlign: "center" }}
        >
          <Box
            sx={{
              mb: -0.6,
              mr: 1.5,
              display: "inline-block",
              height: 20,
              width: 20,
              backgroundColor: chartsColor.blue,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "gray6",
            }}
          />
          One Year Commodity
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "softBlack", textAlign: "center" }}
        >
          <Box
            sx={{
              mb: -0.6,
              mr: 1.5,
              display: "inline-block",
              height: 20,
              width: 20,
              backgroundColor: chartsColor.red,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "gray6",
            }}
          />
          Most Recent Point
        </Typography>
      </Box>
    </Box>
  </Box>
);

export const SimulationModelsModels = memo(
  (props: SimulationModelsModelsProps) => {
    const [currentTab, setCurrentTab] = useState(0);

    const data = useSimulationModelsModels({
      isStatic: props.isStatic,
    });

    useEffect(() => {
      if (
        data.regressionSelector &&
        (data.regressionSelector.value ?? -1) >=
          data.regressionSelector.options.length
      ) {
        data.regressionSelector.onChange(0);
      }
    }, [data.regressionSelector]);

    const pageContent = usePageContent();

    return (
      <TablePlaceHolder
        sx={{ mx: 7, mt: 10 }}
        isLoading={data.assetClassSelector.isLoading || pageContent.isLoading}
        rows={8}
      >
        <CarPageContent
          sx={{ mb: 3 }}
          content={pageContent.getContent(
            CarPageContentCode.SIMULATION_ANALYSIS_MODELS_MODELS,
          )}
          isWhiteContext
        />
        <Box sx={{ mt: 2, display: "flex" }}>
          <CarAssetClassSelector
            sx={{
              width: 300,
              ".MuiInputBase-input": {
                backgroundColor: "white",
              },
            }}
            label="Asset Class"
            {...data.assetClassSelector}
          />
          <CarSelectOptionField
            label="Model Version"
            sx={{
              ml: 4,
              width: 200,
              ".MuiInputBase-input": {
                backgroundColor: "white",
              },
            }}
            {...data.versionSelector}
          />
          <CarDateField
            label="Start Date"
            sx={{
              ml: 4,
              width: 120,
              ".MuiInputBase-root": {
                backgroundColor: "white",
              },
            }}
            value={data.startDate}
            onChange={data.setStartDate}
          />
          <CarDateField
            label="End Date"
            sx={{
              ml: 4,
              width: 120,
              ".MuiInputBase-root": {
                backgroundColor: "white",
              },
            }}
            value={data.endDate}
            onChange={data.setEndDate}
          />
          <CarDownloadButton
            sx={{ ml: "auto", mb: 0.5 }}
            onClick={data.handleDownloadPdf}
          >
            Download PDF
          </CarDownloadButton>
        </Box>
        <TablePlaceHolder
          sx={{ mx: 7, mt: 10 }}
          isLoading={data.isLoading}
          rows={8}
        >
          {isDefined(data.regressionSelector) ? (
            <Box sx={{ mt: 6.5, display: "flex", gap: 5.5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "auto",
                }}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Typography
                    variant="subhSemiBold"
                    sx={{ fontSize: "19px", ml: 11 }}
                  >
                    {data.assetClassSelector.selectedItem?.name} -
                  </Typography>
                  <CarSelectOptionField
                    sx={{
                      width: 300,
                      ".MuiInputBase-input": {
                        backgroundColor: "white",
                      },
                    }}
                    {...data.regressionSelector}
                  />
                </Box>
                <Box
                  sx={{
                    position: "relative",
                    height: SIMULATION_MODELS_CHART_HEIGHT,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: SIMULATION_MODELS_CHART_HEIGHT,
                    }}
                  >
                    {data.selectedRegressionResult?.data ? (
                      <SimulationModelsChart
                        data={data.selectedRegressionResult?.data}
                        isFixedIncome={
                          data.assetClassSelector.selectedItem?.isFixedIncome
                        }
                      />
                    ) : (
                      <SimulationFittedModelsChart
                        data={data.selectedRegressionResult?.fittedModelResult}
                        isFixedIncome={
                          data.assetClassSelector.selectedItem?.isFixedIncome
                        }
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 1.75,
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: "none",
                }}
              >
                <Typography variant="subhSemiBold" sx={{ fontSize: "19px" }}>
                  {`Regression Statistics - ${data.assetClassSelector.selectedItem?.name}`}
                </Typography>
                <CarStatBox
                  sx={{
                    mt: 1.5,
                  }}
                  items={data.generalStat}
                />
                {data.selectedRegressionResult &&
                  data.selectedRegressionResult.statItems.length > 0 && (
                    <>
                      <Typography
                        variant="subhSemiBold"
                        sx={{ mt: 4, fontSize: "19px" }}
                      >
                        {`Regression Statistics - ${data.selectedRegressionResult.label}`}
                      </Typography>
                      <CarStatBox
                        sx={{
                          mt: 1.5,
                        }}
                        items={data.selectedRegressionResult.statItems}
                      />
                    </>
                  )}
                {data.selectedRegressionResult?.data ? (
                  <Legend />
                ) : (
                  <FittedLegend />
                )}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mt: 6.5,
                display: "flex",
                flexDirection: "column",
                flex: "auto",
              }}
            >
              <Typography variant="subhSemiBold" sx={{ fontSize: "19px" }}>
                {`Statistics - ${data.assetClassSelector.selectedItem?.name}`}
              </Typography>
              <CarStatBox
                sx={{
                  mt: 1.5,
                }}
                items={data.generalStat}
              />
            </Box>
          )}

          <CarTextField
            sx={{
              mt: 2,
              width: 610,
              ".MuiInputBase-root": {
                backgroundColor: "white",
              },
            }}
            fullWidth
            label="Calculation Notes"
            multiline
            maxRows={4}
            rows={4}
            InputProps={{
              readOnly: true,
            }}
            value={data.notes}
            onChange={() => {}}
          />
          <CarDownloadButton
            sx={{ mt: 3, ml: "auto" }}
            onClick={data.handleDownloadData}
          >
            Download Data
          </CarDownloadButton>
          <CarFlatPageControl
            sx={{
              ".flat-page-control-header": {
                ml: 3,
                gap: 3,
              },
              ".flat-page-control-button": {
                p: 1,
                fontSize: "18px",
              },
            }}
            items={data.tables.map((i, idx) => ({
              label: i.name,
              value: idx,
              content: (
                <StyledBox>
                  <CarExcelTable table={i} />
                </StyledBox>
              ),
            }))}
            value={currentTab}
            onChange={setCurrentTab}
          />
        </TablePlaceHolder>
      </TablePlaceHolder>
    );
  },
);
